import { createRouter, createWebHistory } from 'vue-router'

import { ensureNoDoubleSlash } from '../mixins/useEnsure'
import { GtmEventEmitter } from '../utils/gtmEventEmitter'
import { useAppStore } from '../stores/AppStore'
import { useCartStore } from '../stores/CartStore'
import { routes } from './routes'
import { nextTick } from 'vue'

const setReferrerHeader = (referrerName) => {
  Object.defineProperty(document, 'referrer', {
    get() {
      return referrerName
    },
  })
}

function delayedScrollBehavior(to, from, savedPosition) {
  return new Promise((resolve) => {
    let userScrolled = false
    const onScroll = () => userScrolled = !ignoreScroll
    window.addEventListener('scroll', onScroll, { once: true, passive: true })

    const waitUntilPageReady = () => {
      // resolve with no scroll if user scrolled
      if (userScrolled) {
        return resolve()
      }

      // resolve with saved position once page is ready
      if (to.meta.pageReady) {
        nextTick(() => {
          window.removeEventListener('scroll', onScroll)
          return resolve(savedPosition)
        })
      }

      requestAnimationFrame(waitUntilPageReady)
    }
    waitUntilPageReady()
  })
}

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    return new Promise((resolve) => {
      if (savedPosition && to.meta.scrollToSavedPositionOnceReady) {
        return resolve(delayedScrollBehavior(to, from, savedPosition))
      }
      setTimeout(() => {
        resolve({ left: 0, top: 0, behavior: 'smooth' })
      }, 100)
    })
  },
})

router.beforeEach(async (to) => {
  const appStore = useAppStore()
  const cartStore = useCartStore()

  if (!appStore.isSignedIn) {
    const data = await appStore.autoLogin()
    if (data?.cart_token) {
      await cartStore.setLocalCart(data.cart_token)
    }
    cartStore.syncCart()
  }

  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth)
  const requiresOpenCart = to.matched.some((record) => record.meta.requiresOpenCart)
  const hasOpenCart = localStorage.cart_token?.length > 0 && cartStore.isCartNotEmpty

  // Resets HTTP referer to avoid creating new sessions of user.
  if (!appStore.getResetReferrer) {
    appStore.setResetReferrer(true)
  } else if (document.referrer !== '') {
    setReferrerHeader('')
  }

  if (requiresAuth && !appStore.isSignedIn) {
    return { name: 'signInPath' }
  } else if (requiresOpenCart && !hasOpenCart) {
    // When open cart is required and no cart is open, redirects to cart path.
    return { name: 'cartsPath' }
  }
  return true
})

router.afterEach(async (to) => {
  const customPageView = to.matched.some((record) => record.meta.customPageView)
  if (!customPageView) {
    GtmEventEmitter.pageView({
      type: to.name,
      url: ensureNoDoubleSlash(`${import.meta.env.VITE_VUE_APP_ORIGINAL_URL}/${to.href}`),
    })
  }
  if (to.query.token || to.query.token === '') {
    const { token, r, ...rest } = to.query
    await router.replace({ query: { ...rest } })
  }
})

// for purposes of ignoring scroll caused by navigation
let ignoreScroll = false
router.afterEach(() => {
  ignoreScroll = true
  setTimeout(() => ignoreScroll = false, 50)
})

export default router
